import React, { Fragment, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Alert, ButtonToolbar } from 'reactstrap';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { reduxForm, Field, change } from 'redux-form';
import ActivityStatusSelector from '../../../../shared/components/form/ActivityStatusSelector';
import SmartCard from '../../../../shared/components/SmartCard';
import DynamicNavigationTabs from '../../../../shared/components/DynamicNavigationTabs';
import TextAreaField from '../../../../shared/components/form/TextAreaField';
import RenderField from '../../../../shared/components/form/RenderField';
import {
  ACTIVITY_STATUS,
  COMMUNICATION_TYPE,
  EXAM_EVALUATION_TYPE,
} from '../../../../shared/utils/constants';
import validate from '../Validate';
import SmartButton from '../../../../shared/components/SmartButton';
import { getFormattedDate } from '../../../../shared/utils/smartutils';

const ActivityOrEvaluationCorrectionForm = (props) => {
  const {
    t,
    dispatch,
    initialize,
    toggleTab,
    correction,
    editable,
    type,
    dto,
    rangeGrade,
    handleSubmit,
  } = props;

  const updateStatus = (updatedStatus) => {
    dispatch(
      change('ActivityOrEvaluationCorrectionForm', 'status', updatedStatus)
    );
  };

  const changeMinAndMaxGrade = () => {
    dispatch(
      change(
        'ActivityOrEvaluationCorrectionForm',
        'minGrade',
        rangeGrade.minGrade
      )
    );
    dispatch(
      change(
        'ActivityOrEvaluationCorrectionForm',
        'maxGrade',
        rangeGrade.maxGrade
      )
    );
  };

  useEffect(() => {
    initialize(correction);
    if (
      correction.status === ACTIVITY_STATUS.PENDING ||
      correction.status === ACTIVITY_STATUS.ANSWERED
    ) {
      dispatch(
        change(
          'ActivityOrEvaluationCorrectionForm',
          'status',
          ACTIVITY_STATUS.CONCLUED
        )
      );
    }
    if (type === COMMUNICATION_TYPE.EXAM) {
      changeMinAndMaxGrade();
      dispatch(
        change(
          'ActivityOrEvaluationCorrectionForm',
          'evaluationType',
          dto.examHeader.evaluationType
        )
      );
    }
    dispatch(change('ActivityOrEvaluationCorrectionForm', 'type', type));
  }, [correction]);

  return (
    <Fragment>
      <SmartCard>
        <form className="form" onSubmit={handleSubmit}>
          {type === COMMUNICATION_TYPE.EXAM &&
            dto.examHeader.evaluationType !== EXAM_EVALUATION_TYPE.CONCEPT && (
              <div className="form__form-group">
                <span className="form__form-group-label bold-text">
                  {t('general.student-score')}
                </span>
                <div className="form__form-group-field">
                  <Field
                    name="grade"
                    type="number"
                    component={RenderField}
                    placeholder={t('general.placeholders.type-here')}
                  />
                </div>
              </div>
            )}
          <div className="form__form-group">
            <span className="form__form-group-label bold-text">
              {t('communication.activityDetails.activityStatusLabel')}
            </span>
            <div className="form__form-group-field pt-3">
              <Field
                name="status"
                component={ActivityStatusSelector}
                t={t}
                updateStatus={updateStatus}
                editable={editable}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label bold-text">
              {t('communication.activityDetails.studyAnswer')}
            </span>
            <div>
              <span className="form__form-group-label">
                {correction.answer}
              </span>
            </div>
          </div>
          {correction && (
            <div className="form__form-group">
              <span className="form__form-group-label bold-text">
                {t('communication.activityDetails.attachmentFilesByStudent')}
              </span>
              <List>
                {correction.taskAttachments.length ? (
                  correction.taskAttachments.map((item) => (
                    <ListItem
                      button
                      key={item.id}
                      component="a"
                      href={item.fileUrl}
                      target="_blank"
                      rel="noopener"
                      className="d-flex"
                    >
                      <ListItemIcon>
                        <OpenInNewIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        className="text-truncate mr-2"
                        disableTypography
                      />
                      <ListItemText
                        primary={`${t('general.send-at')} ${getFormattedDate(
                          item.creationDate
                        )} ${t('general.by')} ${item.responsible.fullName}`}
                        disableTypography
                        className="text-right text-truncate"
                      />
                    </ListItem>
                  ))
                ) : (
                  <Alert className="text-center m-0" color="primary">
                    {t('general.no-file-attached')}
                  </Alert>
                )}
              </List>
            </div>
          )}
          <div className="form__form-group">
            <DynamicNavigationTabs>
              <div
                className="form__form-group--centered"
                title={t('general.public-obs')}
              >
                <div className="form__form-group">
                  <div className="form__form-group-field">
                    <Field
                      name="publicObs"
                      component={TextAreaField}
                      maxLength="500"
                      disabled={editable}
                      placeholder={t('general.placeholders.public-obs')}
                    />
                  </div>
                </div>
              </div>
              <div
                className="form__form-group"
                title={t('general.private-obs')}
              >
                <div className="form__form-group-field">
                  <Field
                    name="privateObs"
                    component={TextAreaField}
                    maxLength="500"
                    disabled={editable}
                    placeholder={t('general.placeholders.private-obs')}
                  />
                </div>
              </div>
            </DynamicNavigationTabs>
          </div>
          <ButtonToolbar>
            <SmartButton
              label={t('general.back')}
              color="primary"
              onClick={toggleTab}
            />
            <SmartButton
              label={t('general.save')}
              disabled={editable}
              color="success"
              type="submit"
            />
          </ButtonToolbar>
        </form>
      </SmartCard>
    </Fragment>
  );
};

ActivityOrEvaluationCorrectionForm.propTypes = {
  t: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  toggleTab: PropTypes.func.isRequired,
  correction: PropTypes.shape(PropTypes.shape).isRequired,
  initialize: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  rangeGrade: PropTypes.shape(PropTypes.number).isRequired,
};

export default reduxForm({
  form: 'ActivityOrEvaluationCorrectionForm',
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
  validate,
})(withTranslation('common')(ActivityOrEvaluationCorrectionForm));
